import React from "react";
import styled, { keyframes, css } from "styled-components";
import { GRAY_50, GRAY_100, PRIMARY_50 } from "../../constant/colors";

import { fontWeights, sizes } from "../../constant/styles";
import { CONSENT_STATUS_COLORS, CONSENT_STATUS_TYPES } from "../../constant/consent";

const highlightFade = keyframes`
  0% {
    background-color: ${PRIMARY_50};
  }
  57% {
    background-color: ${PRIMARY_50};
  }
  100% {
    background-color: transparent;
  }
`;

const EventContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 5px;
  width: 100%;

  ${(props) =>
    props.isHighlighted &&
    css`
      animation: ${highlightFade} 3.5s ease-out forwards;
      border-radius: 8px;
      padding: 8px;
      margin: -8px;
    `}
`;

const EventMainRow = styled.div`
  display: flex;
  align-self: stretch;
  gap: 10px;
  width: 100%;
`;

const EventContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
`;

const EventTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const EventIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  background-color: ${GRAY_50};
  border-radius: 100px;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const EventTitle = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.medium};
  line-height: 1.5em;
`;

const EventSubtitle = styled.div`
  font-size: ${sizes.small};
  line-height: 1.5em;
`;

const EventStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 5px;
  background-color: ${GRAY_100};
  border-radius: 5px;
  flex-shrink: 0;
  height: 24px;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  flex-shrink: 0;
`;

const StatusText = styled.span`
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.small};
  line-height: 1;
  white-space: nowrap;
`;

const EventDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`;

const ConsentEventItem = ({
  icon,
  title,
  subtitle,
  status,
  statusText,
  renderDetails = null,
  isHighlighted = false,
  eventId
}) => {
  const statusColor = CONSENT_STATUS_COLORS[status] || CONSENT_STATUS_COLORS[CONSENT_STATUS_TYPES.NO_RECORD];

  return (
    <EventContainer id={`consent-event-${eventId}`} isHighlighted={isHighlighted}>
      <EventMainRow>
        <EventIcon>{icon}</EventIcon>
        <EventContent>
          <EventTitleRow>
            <EventTitle>{title}</EventTitle>
            <EventStatus>
              <StatusDot color={statusColor} />
              <StatusText>{statusText}</StatusText>
            </EventStatus>
          </EventTitleRow>
          {subtitle && <EventSubtitle id={`consent-event-subtitle-${eventId}`}>{subtitle}</EventSubtitle>}
          {/* Additional details rendered by specific event types - now aligned with content */}
          {renderDetails && <EventDetails id={`consent-event-details-${eventId}`}>{renderDetails()}</EventDetails>}
        </EventContent>
      </EventMainRow>
    </EventContainer>
  );
};

export default ConsentEventItem;
